import { Components, Data, DateUtils, List, Misc, NeoModel } from '@singularsystems/neo-core';
import { Routing, Views } from '@singularsystems/neo-react';
import { NotificationDuration } from '../../../App/Models/NotificationDuration';
import { AppService, DomainTypes, Types } from '../../DomainTypes';
import NewIssuerInvestmentCommand from '../../Models/IssuerInvestments/Commands/NewIssuerInvestmentCommand';
import IssuerInvestment from '../../Models/IssuerInvestments/IssuerInvestment';
import BranchDetailLookup from "../../Models/Catalogue/Reporting/Lookups/BranchDetailLookup";
import IssuerInvestmentPagedLookup from '../../Models/IssuerInvestments/Lookups/IssuerInvestmentPagedLookup';
import IssuerInvestmentPagedLookupCriteria from '../../Models/IssuerInvestments/Lookups/IssuerInvestmentPagedLookupCriteria';
import NoteVM from "../../Models/Notes/NoteVM";
import LiquidityProvider from '../../Models/InvestorInvestments/LiquidityProvider';
import LinkedInvestorInvestmentLookup from '../../Models/InvestorInvestments/Lookups/LinkedInvestorInvestmentLookup';
import InvestorInvestment from "../../Models/InvestorInvestments/InvestorInvestment";
import IssuerVM from '../Issuer/IssuerVM';
import ParticipationAgreementLookup from '../../Models/IssuerInvestments/Lookups/ParticipationAgreementLookup';
import RollbackAllocationsCommand from "../../Models/IssuerInvestments/Commands/RollbackAllocationsCommand";
import AuthorisationVM from "../../Services/AuthorisationVM";

@NeoModel
export default class IssuerInvestmentVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public authorisation = AppService.get(Types.Neo.Security.AuthorisationService),
        public dataCache = AppService.get(DomainTypes.Services.DataCache),
        public issuerInvestmentsApiClient = AppService.get(DomainTypes.ApiClients.IssuerInvestment),
        public reportingApiClient = AppService.get(DomainTypes.ApiClients.CatalogueReporting),
        public issuerApiClient = AppService.get(DomainTypes.ApiClients.Issuer),
        public investorInvestmentApiClient = AppService.get(DomainTypes.ApiClients.InvestorInvestment),
        private issuerInvestmentCache = AppService.get(DomainTypes.Services.IssuerInvestmentCache),
        public documentsApiClient = AppService.get(DomainTypes.ApiClients.Documents)) {

        super(taskRunner);

        this.searchAutoRunner = this.criteria.onAnyPropertyChanged(() => this.pageManager.refreshData());
    }

    public noteVM = this.registerViewModel(NoteVM);
    public authorisationVM = this.registerViewModel(AuthorisationVM);

    public fileManager = new Components.FileManager(this.issuerInvestmentsApiClient.getUploadParticipationAgreementFileUrl(), {
        afterUpload: this.afterUpload.bind(this),
        multiple: false,
        additionalData: () => { return { issuerInvestmentId: this.issuerInvestment!.issuerInvestmentId } }
    });

    public liquidityProviderList = new List(LiquidityProvider);

    public searchAutoRunner: Misc.IAutoRunner;

    public criteria = new IssuerInvestmentPagedLookupCriteria();
    public pageManager = new Data.PageManager(this.criteria, IssuerInvestmentPagedLookup, this.issuerInvestmentsApiClient.getIssuerInvestmentsPaged,
        { fetchInitial: true, sortBy: "issuerInvestmentId" });

    public issuerInvestment: IssuerInvestment | null = null;
    public linkedInvestorInvestments = new List(LinkedInvestorInvestmentLookup);

    public newIssuerInvestment: NewIssuerInvestmentCommand | null = null;

    public rollbackAllocationsCommand: RollbackAllocationsCommand | null = null;

    public branchDataSource = new Data.ApiClientLookupDataSource(this.reportingApiClient.getBranchDetailLookups, BranchDetailLookup);

    public selectedTab = "Details";

    public async initialise() {
        
    }

    private afterUpload(result: any) {
        if (this.issuerInvestment && result) {
            var lookup = ParticipationAgreementLookup.fromJSObject<ParticipationAgreementLookup>(result);
            this.issuerInvestment.lookup.participationAgreements.push(lookup);
            this.notifications.showMessage("Participation Agreement Uploaded", "The participation agreement was uploaded successfully");
        }
    }

    public async setCriteriaIssuer(issuerId: number | null = null) {
        if (issuerId === IssuerVM.lastIssuer?.issuerId) {
            this.criteria.issuerId = issuerId;
            this.criteria.issuerName = IssuerVM.lastIssuer?.issuerName;
            // Initial fetch will still happen on render.
            this.searchAutoRunner.cancelPending();
        }
    }

    public async newInvestment() {
        const command = new NewIssuerInvestmentCommand();

        if (this.criteria.issuerId) {
            const issuer = await this.taskRunner.waitForData(this.issuerApiClient.getIssuerLookup(this.criteria.issuerId));
            command.setIssuer(issuer);
        }

        this.newIssuerInvestment = command;
        this.liquidityProviderList = new List(LiquidityProvider);
        this.selectedTab = "Details";
    }

    public async loadInvestment(viewParam: Routing.IViewParameter) {

        const data = await this.taskRunner.waitForData(this.issuerInvestmentsApiClient.getIssuerInvestment(viewParam.value as number));
        this.issuerInvestment = IssuerInvestment.fromJSObject<IssuerInvestment>(data);
        this.selectedTab = "Investment";

        viewParam.description = `${data.issuerInvestmentId} - ${data.lookup!.issuerName}`;
    }

    public async getLinkedInvestorInvestments() {
        if (this.issuerInvestment) {
            const data = await this.taskRunner.waitForData(this.issuerInvestmentsApiClient.getLinkedInvestorInvestments(this.issuerInvestment.issuerInvestmentId));
            this.linkedInvestorInvestments.set(data);
        }
    }

    public async createNewInvestment() {
        if (this.newIssuerInvestment) {
            const issuerInvestment = IssuerInvestment.fromCreateCommand(this.newIssuerInvestment);

            if (this.newIssuerInvestment.investorInvestmentId) {
                // Issuer investments linked to an investor investment share the same investment.
                // Load the investor investment in order to get the investment details.
                const response = await this.taskRunner.waitFor(this.investorInvestmentApiClient.getInvestorInvestment(this.newIssuerInvestment.investorInvestmentId));
                const investorInvestment = InvestorInvestment.fromJSObject<InvestorInvestment>(response.data);
                issuerInvestment.investment = investorInvestment.investment;
                issuerInvestment.investmentId = investorInvestment.investmentId;
            }

            this.issuerInvestment = issuerInvestment;
            this.newIssuerInvestment = null;
        }
    }

    public async fetchLiquidityProviders() {
        if (this.issuerInvestment) {
            const lpData = await this.taskRunner.waitForData(this.issuerInvestmentsApiClient.getLiquidityProviders(this.issuerInvestment.issuerInvestmentId));
            this.liquidityProviderList.set(lpData);
        }
    }

    public async fetchQPTracking() {
        if (this.issuerInvestment) {
            const qpTrackingData = await this.taskRunner.waitForData(this.issuerInvestmentsApiClient.getQPTrackingList(this.issuerInvestment.issuerInvestmentId));
            this.issuerInvestment.qpTracking.set(qpTrackingData);
        }
    }

    public async saveLiquidityProvider() {
        if (this.liquidityProviderList.isValid) {
            this.taskRunner.run(async () => {
                var response = await this.issuerInvestmentsApiClient.saveLiquidityProviders(this.liquidityProviderList.toJSArray());
                this.liquidityProviderList!.update(response.data);
                this.notifications.addSuccess("Saved Successfully", "Liquidity Providers saved successfully.", NotificationDuration.Standard);
            });
        }
    }

    public async saveInvestment() {
        await this.taskRunner.run(async () => {
            if (this.issuerInvestment) {
                const dateCycleIsNew = this.issuerInvestment.investment.dividendCycle?.isNew;
                const response = await this.issuerInvestmentsApiClient.saveIssuerInvestment(this.issuerInvestment.toJSObject());
                this.issuerInvestment.investment.afterSave(response.data);
                this.issuerInvestment.update(response.data.investment);
                this.pageManager.refreshPage();
                this.issuerInvestmentCache.updateParentInvestments(true);

                if (dateCycleIsNew) {
                    this.dataCache.dateCycles.expire();
                }

                this.notifications.addSuccess("Save", "Investment saved successfully.", NotificationDuration.Standard);
            }
        });
    }

    public setIssuerInvestmentPendingDelete(issuerInvestment: IssuerInvestment) {
        issuerInvestment.isPendingDelete = !issuerInvestment.isPendingDelete;
        if (issuerInvestment.isPendingDelete) {
            this.notifications.addInfo("Mark Deleted", `Please click save to action this request.`, NotificationDuration.Standard);
        }
    }

    public async downloadParticipationAgreementFile(fileDescriptorId: number) {
        var downloadUrl = await this.taskRunner.waitForData(this.documentsApiClient.getFileInlineUrl(fileDescriptorId));
        window.open(downloadUrl, "_tab");
    }

    public beginRollbackAllocations() {
        if (this.issuerInvestment) {
            const command = new RollbackAllocationsCommand();
            command.issuerInvestmentId = this.issuerInvestment.issuerInvestmentId;
            command.fromDate = this.issuerInvestment.lastAllocationDate;
            this.rollbackAllocationsCommand = command;
        }
    }

    public async rollbackAllocations() {
        if (this.rollbackAllocationsCommand && this.issuerInvestment) {
            await this.taskRunner.run(() => this.issuerInvestmentsApiClient.rollbackShareAllocations(this.rollbackAllocationsCommand!.toJSObject()));
            this.notifications.addSuccess("Allocations Rolled Back", "The allocations for this investment have been rolled back successfully.", NotificationDuration.Standard);
            this.issuerInvestment.lastAllocationDate = DateUtils.add(this.rollbackAllocationsCommand.fromDate!, { days: -1 });
            this.rollbackAllocationsCommand = null;
        }
    }

    public deleteParticipationAgreements(participationAgreement: ParticipationAgreementLookup) {
        return this.taskRunner.run(async () => {
            await this.issuerInvestmentsApiClient.deleteParticipationAgreement(participationAgreement.participationAgreementId);
            this.issuerInvestment?.lookup.participationAgreements.remove(participationAgreement);
            this.notifications.addSuccess("Delete Successful", "Participation Agreement was deleted successfully.", NotificationDuration.Standard);
        });
    }
}